import React, { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { Box, Modal, DialogActions, DialogContent, DialogTitle, Button, TextField, MenuItem, Select, InputLabel, FormControl, Grid, CircularProgress } from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch, useSelector } from 'react-redux';
import ProjectResources from './ProjectResources';
import TaskList from './TaskList';
import AddDiscussionModal from './ProjectDiscussionModal';
import { createProject, deleteProject, updateProject } from '../../api/projectsApi';
import { fetchProjects } from '../../store/slices/projectsSlice';
import { fetchDiscussions } from '../../store/slices/discussionsSlice';
import { storage } from '../../firebaseconfig';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

const ProjectManagement = () => {
  const dispatch = useDispatch();
  const [projects, setProjects] = useState([]);
  const adminId = useSelector((state) => state?.users?.id);
  const [tasks, setTasks] = useState([]);
  const [projectDiscussion, setProjectDiscussion] = useState([]);
  const [activeTabs, setActiveTabs] = useState({});
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [newProject, setNewProject] = useState({ title: '', category: '', description: '', pdfUrl: '' });
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [openDiscussionModal, setOpenDiscussionModal] = useState(false);
  const [newDiscussion, setNewDiscussion] = useState({ title: '', userId: adminId, description: '', author: 'CurrentUser', authorImage: adminId?.profileImage, date: new Date().toLocaleDateString(), views: 0, likes: 0, topic: '' });
  const [newTopicInput, setNewTopicInput] = useState('');
  const [loading, setLoading] = useState(false);

  const { projects: projectData } = useSelector((state) => state.projects);

  useEffect(() => {
    dispatch(fetchProjects(adminId));
  }, [dispatch, adminId]);

  useEffect(() => {
    dispatch(fetchDiscussions(adminId));
  }, [dispatch, adminId]);

  const handlepdfUpload = async (file) => {
    if (!file) return;

    setLoading(true);
    const storageRef = ref(storage, `pdfs/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      null,
      (error) => {
        toast.error('Error uploading PDF: ' + error.message);
        setLoading(false);
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        setNewProject((prev) => ({ ...prev, pdfUrl: downloadURL }));
        setLoading(false);
        toast.success('File uploaded successfully');
      }
    );
  };

  const handleTabChange = (projectId, tab) => {
    setActiveTabs((prevTabs) => ({
      ...prevTabs,
      [projectId]: prevTabs[projectId] === tab ? null : tab,
    }));
  };
  const handleCreateProject = async () => {
    if (!newProject.title.trim() || !newProject.category || !newProject.description.trim() || !newProject.pdfUrl) {
      toast.error('All fields, including PDF, are required.');
      return;
    }

    const data = {
      userId: adminId,
      title: newProject.title.trim(),
      category: newProject.category,
      description: newProject.description.trim(),
      pdfUrl: newProject.pdfUrl
    };

    try {
      const response = await createProject(data);
      setNewProject({ title: '', category: '', description: '', pdfUrl: '' });
      setShowNewProjectModal(false);
      toast.success('Project created successfully!');

      if (response?.data?.id) {
        setProjects((prevProjects) => [...prevProjects, { ...data, id: response.data.id }]);
      }
    } catch (error) {
      toast.error('Error creating project: ' + error.message);
    }
  };
  const handleEditProject = (project) => {
    setEditProject(project);
    setShowEditProjectModal(true);
  };

  const handleUpdateProject = async () => {
    if (!editProject.title.trim() || !editProject.category || !editProject.description.trim()) {
      toast.error('All fields are required.');
      return;
    }

    try {
      await updateProject(editProject.id, editProject);
      setProjects((prevProjects) =>
        prevProjects.map((project) =>
          project.id === editProject.id ? editProject : project
        )
      );
      setShowEditProjectModal(false);
      toast.success('Project updated successfully!');
    } catch (error) {
      toast.error('Error updating project: ' + error.message);
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      await deleteProject(projectId);
      toast.success('Project deleted successfully!');
      setProjects((prevProjects) => prevProjects.filter((project) => project.id !== projectId));
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-gray-50 to-gray-100 p-8 transition-all duration-300">
      <div className="max-w-8gl mx-auto py-12 px-4 sm:px-6 lg:xl p-6 border border-gray-300 rounded-lg shadow-lg bg-white dark:bg-gray-800">
        <h2 className="text-2xl relative z-20 md:text-4xl lg:text-7xl font-bold text-center text-black dark:text-white font-sans tracking-tight">
          Welcome to<br />
          <div
            className="relative mx-auto inline-block w-max"
          >
            <div className="absolute left-0 top-[1px] bg-clip-text bg-no-repeat text-transparent bg-gradient-to-r py-4 from-purple-500 via-violet-500 to-pink-500">
              <span className="">Project Management.</span>
            </div>
            <div className="relative bg-clip-text text-transparent bg-no-repeat bg-gradient-to-r from-purple-500 via-violet-500 to-pink-500 py-4">
              <span className="">Project Management.</span>
            </div>
          </div>
        </h2>
      </div>
      <ToastContainer />
      <div className="flex justify-between items-center my-8">
        <h1 className="text-3xl font-semibold text-gray-800 tracking-wide">
          Create a New Project Management
        </h1>
        <button
          onClick={() => setShowNewProjectModal(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700"
        >
          <Plus size={20} /> New Project
        </button>
      </div>

      <div className="grid grid-cols-1 gap-8">
        {projectData.map((project, index) => (
          <div key={index} className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-2xl font-bold">{project.title}</h2>
                <p className="text-gray-500">{project.category}</p>
              </div>
              <div className="flex gap-4">
                {adminId === project.userId && (
                  <>
                    <button className="text-blue-600" onClick={() => handleEditProject(project)}>Edit</button>
                    <button className="text-red-600" onClick={() => handleDeleteProject(project.id)}>Delete</button>
                  </>
                )}
              </div>
            </div>

            <div className="border-b mb-4">
              <nav className="flex gap-4">
                {['overview', 'tasks', 'resources'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => handleTabChange(project.id, tab)}
                    className={`py-2 ${activeTabs[project.id] === tab
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-600'
                      }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </nav>
            </div>

            <div className="mt-4">
              {activeTabs[project.id] === 'overview' && (
                <div>
                  <h3 className="text-lg font-medium">Timeline</h3>
                  <p>{project.description}</p>
                </div>
              )}
              {activeTabs[project.id] === 'tasks' && (

                <TaskList
                  project={project}
                  userId={adminId}
                />
              )}
              {activeTabs[project.id] === 'resources' && (
                <div>
                  <ProjectResources
                    projectId={project.id}
                    fetchProjectResources={projectData}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* New Project Modal */}
      <Modal open={showNewProjectModal} onClose={() => setShowNewProjectModal(false)}>
        <Box className="bg-white p-6 rounded-lg w-1/3 mx-auto mt-20">
          <DialogTitle>Create New Project</DialogTitle>
          <DialogContent>
            <TextField
              label="Project Title"
              fullWidth
              value={newProject.title}
              onChange={(e) => setNewProject({ ...newProject, title: e.target.value })}
              margin="normal"
            />
            <TextField
              label="Description"
              fullWidth
              value={newProject.description}
              onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Category</InputLabel>
              <Select
                value={newProject.category}
                onChange={(e) => setNewProject({ ...newProject, category: e.target.value })}
              >
                <MenuItem value="Artificial Intelligence">Artificial Intelligence</MenuItem>
                <MenuItem value="Software Development">Software Development</MenuItem>
                <MenuItem value="Web Development">Web Development</MenuItem>
              </Select>
            </FormControl>
            <input type="file" accept=".pdf" onChange={(e) => handlepdfUpload(e.target.files[0])} />
            {loading && <CircularProgress size={24} />}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowNewProjectModal(false)}>Cancel</Button>
            <Button onClick={handleCreateProject} disabled={loading}>
              {loading ? 'Uploading...' : 'Create'}
            </Button>
          </DialogActions>
        </Box>
      </Modal>

      {/* Edit Project Modal */}
      <Modal open={showEditProjectModal} onClose={() => setShowEditProjectModal(false)}>
        <Box className="bg-white p-6 rounded-lg w-1/3 mx-auto mt-20">
          <DialogTitle>Edit Project</DialogTitle>
          <DialogContent>
            <TextField
              label="Project Title"
              fullWidth
              value={editProject?.title || ''}
              onChange={(e) =>
                setEditProject((prev) => ({ ...prev, title: e.target.value }))
              }
              margin="normal"
            />
            <TextField
              label="Description"
              fullWidth
              value={editProject?.description || ''}
              onChange={(e) =>
                setEditProject((prev) => ({ ...prev, description: e.target.value }))
              }
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Category</InputLabel>
              <Select
                value={editProject?.category || ''}
                onChange={(e) =>
                  setEditProject((prev) => ({ ...prev, category: e.target.value }))
                }
              >
                <MenuItem value="Artificial Intelligence">Artificial Intelligence</MenuItem>
                <MenuItem value="Software Development">Software Development</MenuItem>
                <MenuItem value="Web Development">Web Development</MenuItem>
              </Select>
            </FormControl>
            <input type="file" onChange={(e) => handlepdfUpload(e.target.files[0])} />
            {loading && <CircularProgress size={24} />}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowEditProjectModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateProject} disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Box>
      </Modal>

      {/* Add Discussion Modal */}
      <AddDiscussionModal
        open={openDiscussionModal}
        handleClose={() => setOpenDiscussionModal(false)}
        newDiscussion={newDiscussion}
        setNewDiscussion={setNewDiscussion}
        // handleAddDiscussion={handleAddDiscussion}
        uniqueTopics={[...new Set(projectDiscussion.map((d) => d.topic))]}
        newTopicInput={newTopicInput}
        setNewTopicInput={setNewTopicInput}
      />
    </div>
  );
};

export default ProjectManagement;
